import Vue from 'vue'
import Vuex from 'vuex'
import modules from './modules'
import VuexPersist from 'vuex-persist'
import VerofreshRecord from '@/orm/models/VerofreshRecord'
import { $orm } from '@/orm'
import { isObject, forEach } from 'lodash'
import buildInfo from '@/assets/build.json'

Vue.use(Vuex)
const CircularJSON = require('circular-json')

const STORAGE_KEY = 'veroFreshApp'
const VERSION_KEY = 'veroFreshAppVersion'
const CIRCUIT_BREAKER_KEY = 'veroFreshAppCacheCleared'
const CIRCUIT_BREAKER_TTL_MS = 30 * 1000
const DEV_VERSION = 'pre-build'

export function checkVersionAndClearIfNeeded (currentVersion) {
  if (currentVersion === DEV_VERSION) return

  const storage = window.localStorage
  const storedVersion = storage.getItem(VERSION_KEY)

  if (storedVersion === null) {
    storage.setItem(VERSION_KEY, currentVersion)
    return
  }

  if (storedVersion === currentVersion) return

  const lastCleared = parseInt(storage.getItem(CIRCUIT_BREAKER_KEY), 10)
  const now = Date.now()
  if (!isNaN(lastCleared) && (now - lastCleared) < CIRCUIT_BREAKER_TTL_MS) {
    return
  }

  storage.removeItem(STORAGE_KEY)
  storage.setItem(VERSION_KEY, currentVersion)
  storage.setItem(CIRCUIT_BREAKER_KEY, String(now))
  window.location.reload()
}

checkVersionAndClearIfNeeded(buildInfo.version)

const vuexLocalStorage = new VuexPersist({
  key: STORAGE_KEY,

  /**
   * js-data Records don't naturally preserve relationships when converted to
   * json. Because of that when we store js-data records in our state we need to
   * take an extra step to ensure we save relations.
   *
   * @param string key
   * @param object state
   * @return undefined
   */
  saveState: (key, state) => {
    const newState = {}
    forEach(state, (storedModule, moduleName) => {
      newState[moduleName] = {}
      forEach(storedModule, (value, key) => {
        if (isObject(value) && value instanceof VerofreshRecord) {
          // Objects returned from localStorage naturally come back as plain
          // objects, however js-data objects expose a bunch of handy functions
          // that we want to preserve. In order for our restore process to
          // properly restore js-data records as their original instances we
          // need to add a little flag that indicates the object was an instance
          // of VerofreshRecord (the original class name is already stored on
          // the object)
          value.$localStorageProto = 'VerofreshRecord'

          // Here the withAll flag instructs the json function to include all
          // relations
          newState[moduleName][key] = value.toJSON({ withAll: true })
        } else {
          newState[moduleName][key] = value
        }
      })
    })

    window.localStorage.setItem(key, CircularJSON.stringify(newState))
  },

  restoreState: safeRestoreState
})

export function safeRestoreState (key, storage) {
  try {
    const value = storage.getItem(key)
    const state = (typeof value === 'string') ? CircularJSON.parse(value || '{}') : (value || {})

    forEach(state, (storedModule, moduleName) => {
      forEach(storedModule, (value, key) => {
        if (isObject(value) && value.$localStorageProto === 'VerofreshRecord') {
          delete value.$localStorageProto
          const mapperName = value.$className.slice(0, -6)
          state[moduleName][key] = $orm(mapperName).createRecord(value)
        }
      })
    })

    return state
  } catch (error) {
    console.error('[veroFresh] corrupt persisted state — clearing', error)
    window.localStorage.removeItem(key)
    return {}
  }
}

const store = new Vuex.Store({
  modules,
  plugins: [vuexLocalStorage.plugin]
})

export default store
